import type { DxfEntityKey, DxfMappingRole } from '@frontend/api';
import { type ReadableStore, deriveStore } from '@frontend/viewer-core';

import { entityKeyPolylines, flattenPolylinesToSegments } from './mappingModel';
import { MAPPING_ROLES_IN_DRAW_ORDER } from './roles';
import type {
  AccentBatch,
  DrawingModel,
  DxfSessionState,
  RoleIndex,
} from './types';

/**
 * Fires when anything the role overlay is drawn from changes, and not otherwise.
 *
 * Deliberately *not* `accentSource`. That slice includes selection and hover, which
 * change on every click and every pointer move over geometry; rebuilding the role
 * overlay on those would re-flatten the whole drawing continuously. This is the
 * separation that keeps hovering cheap — see `setOverlayBatches` on the renderer,
 * which is a second group for the same reason.
 */
export const createRoleOverlaySourceSlice = (
  state: ReadableStore<DxfSessionState>,
): ReadableStore<unknown> =>
  deriveStore(
    state,
    (current) =>
      [
        current.mapping.draft,
        current.mapping.saved,
        current.overlays.mappingColoring,
        current.hiddenLayers,
      ] as const,
    (a, b) => a.every((value, index) => Object.is(value, b[index])),
  );

type CacheEntry = {
  model: DrawingModel;
  keys: ReadonlySet<DxfEntityKey>;
  hiddenLayers: ReadonlySet<number>;
  polylines: readonly (readonly number[])[];
  /** Only the solid path draws this; computed alongside `polylines` anyway. */
  positions: number[];
};

/**
 * A dashed batch's `positions` field goes unused — the renderer draws its
 * `polylines` instead — so every dashed batch shares this one empty array
 * rather than each allocating its own. Also what keeps `[] !== []` reference
 * churn out of a memoisation check that compares a dashed role's output
 * across two builds.
 */
const NO_POSITIONS: readonly number[] = [];

/**
 * Builds the per-role stroke batches the renderer paints under the interaction
 * accents.
 *
 * A factory rather than a bare function because it carries a cache. Flattening a
 * role means walking every chunk of every entity in it, and a single assignment
 * usually touches two roles out of four — so rebuilding all of them on each edit
 * throws away most of the work. Each role's geometry is kept against the exact
 * inputs it was built from (`roleIndex` hands back a fresh `Set` per role only when
 * that role actually changed), and an untouched role is reused outright.
 */
export const createRoleBatchBuilder = () => {
  const cache = new Map<DxfMappingRole, CacheEntry>();

  return (
    model: DrawingModel | null,
    roleIndex: RoleIndex,
    hiddenLayers: ReadonlySet<number>,
  ): AccentBatch[] => {
    if (!model || roleIndex.size === 0) {
      cache.clear();
      return [];
    }

    const batches: AccentBatch[] = [];

    MAPPING_ROLES_IN_DRAW_ORDER.forEach(({ role, stroke }) => {
      const keys = roleIndex.get(role);
      if (!keys || keys.size === 0) {
        cache.delete(role);
        return;
      }

      const cached = cache.get(role);
      const isCacheValid =
        cached &&
        cached.model === model &&
        cached.keys === keys &&
        cached.hiddenLayers === hiddenLayers;
      const polylines = isCacheValid
        ? cached.polylines
        : entityKeyPolylines(keys, model, hiddenLayers);
      // A solid role needs the flattened form; computed alongside the
      // polylines it was derived from, so a cache hit reuses both rather than
      // re-flattening on every call regardless of whether this role dashes.
      const positions = isCacheValid
        ? cached.positions
        : flattenPolylinesToSegments(polylines);

      cache.set(role, { model, keys, hiddenLayers, polylines, positions });

      // A role can be entirely hidden, or consist only of entities with no
      // stitched geometry — an empty batch would allocate a buffer for nothing.
      if (polylines.length === 0) {
        return;
      }
      batches.push(
        stroke.dash
          ? {
              // Drawn from `polylines` as joined `Line2`s, not this flat
              // buffer — see `entityKeyPolylines` for why a dashed role
              // cannot be drawn from disconnected segments.
              positions: NO_POSITIONS,
              color: stroke.colorHex,
              widthPx: stroke.widthPx,
              dash: stroke.dash,
              polylines,
            }
          : {
              positions,
              color: stroke.colorHex,
              widthPx: stroke.widthPx,
              dash: null,
            },
      );
    });

    return batches;
  };
};
